import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import CouponSaving from 'Components/Coupon/CouponSaving';
import Drm from 'Components/Utility/Drm';
import GiftCardEditions from 'Features/Product/components/ProductCommerce/GiftCardEditions';
import HowToGiftBanner from 'Components/Gift/HowToGiftBanner';
import LegalNotice from 'Features/Product/components/Legal/LegalNotice';
import PaymentCards from 'Components/PaymentCards/PaymentCards';
import Price from 'Components/Utility/Price/Price';
import ProductAboutSection from 'Features/Product/components/ProductAboutSection/ProductAboutSection';
import ProductAddToCartBtn from 'Components/ProductAddToCartBtn/ProductAddToCartBtn';
import ProductBreadcrumbs from 'Components/Utility/SEO/ProductBreadcrumbs';
import ProductCouponPrice from 'Components/Price/ProductCouponPrice';
import ProductCover from 'Components/Utility/ProductCover';
import ProductDiscountPrice from 'Components/Price/ProductDiscountPrice';
import ProductEditionSelector from 'Features/Product/components/ProductCommerce/ProductEditionSelector';
import ProductMessages from 'Features/Product/components/ProductMessages/ProductMessages';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import ProductReviewsContext from 'Features/Product/context/product-reviews-context';
import ProductSEOHelmet from 'Components/Utility/SEO/ProductSEOHelmet';
import ProductStructuredData from 'Components/Utility/SEO/ProductStructuredData';
import RegionLocked from 'Components/Regional/RegionLocked';
import WishlistButton from 'Components/WishlistButton/WishlistButton';


import 'Features/Product/components/Views/GiftCardView.scss';
import 'Features/Product/components/Views/ProductView.scss';

const GiftCardView = ({
  product,
}) => {
  const voucher = useSelector(state => state?.voucher);
  const currentDrm = ProductRequirements.CurrentDrm(product);
  const validCoupon = ProductRequirements.validCoupon(product, voucher);
  const totalSaving = ProductRequirements.totalSaving(product, validCoupon);
  const currentDiscount = product.current_discount;
  const { editions } = product;
  const { pathname } = useLocation();


  let editionSelectorToUse = null;

  if (editions && editions.length) {
    if (
      editions[0].name &&
      editions[0].name.match(/\$|£|€|₽|¥/)) {
      editionSelectorToUse = 'giftcard';
    } else if (editions[0].name && editions[0].name.match(/membership|subscription/i)) {
      editionSelectorToUse = 'membership';
    } else {
      editionSelectorToUse = 'product';
    }
  }

  const { reviews } = useContext(ProductReviewsContext);

  return (
    <main key="gift-card" className="GiftCardPage new-product-page">
      <ProductBreadcrumbs product={product} />
      <ProductSEOHelmet product={product} />
      <ProductStructuredData product={product} reviews={reviews} />
      <div className="GiftCardPage__header page-section-margin container">
        <div className="name-timer-container mobile-bundle-detail-container">
          <div className="name-container">
            <h1 className="product-name">
              {product.name}
            </h1>
          </div>
        </div>

        <div className="GiftCardPage__headerContent">
          <div className="cover-container">
            <ProductCover
              cover={product.cover}
              alt={`Product cover for ${product.name}`}
              sizesOverride="(min-width: 1520px) 662px, (min-width: 1200px) 562px, (min-width: 992px) 472px, (min-width: 768px) 352px, (min-width: 576px) 524px, 98vw"
              displayType={product.displayType}
              coverType={product.cover_type}
            />
          </div>
          <div className="details-container">
            <div className="w-100">
              <div className="icons-wishlist-container">
                <div className={`drm-container drm-container-${currentDrm}`}>
                  <Drm drmType={currentDrm} />
                </div>
              </div>

              {editionSelectorToUse === 'giftcard' &&
                <React.Fragment>
                  <h2 className="h3">
                    <FormattedMessage id="giftcardpage.choosegiftcardamount" defaultMessage="Choose Gift Card Amount" />
                  </h2>

                  <GiftCardEditions product={product} />
                </React.Fragment>
              }

              {editionSelectorToUse === 'membership' &&
                <h2 className="h3">
                  <FormattedMessage id="giftcardpage.choosemembership" defaultMessage="Membership Options:" />
                </h2>
              }

              {(editionSelectorToUse === 'product' || editionSelectorToUse === 'membership') &&
                <ProductEditionSelector sortByName product={product} />
              }

              {validCoupon &&
                <ProductCouponPrice
                  price={product.price}
                  type={product.is_srp_bundle ? null : product.type}
                  currentDiscount={currentDiscount}
                  couponDiscountPercent={(get(validCoupon, 'percent', 0) / 100)}
                  totalSavingPercent={totalSaving.percent}
                  hideDiscount={product.hideDiscount}
                  soldOut={product.soldOut}
                  slug={product.slug}
                  mystery={product.mystery}
                />
              }

              {validCoupon &&
                <CouponSaving
                  savingPrice={totalSaving.price}
                  hasExtraSaving={totalSaving.hasExtraSaving}
                  extraSavingPrice={totalSaving.extraSavingPrice}
                  coupon={validCoupon}
                  isMonetary={totalSaving.isMonetary}
                />
              }

              {!currentDiscount && !validCoupon &&
                <div className="price-container">
                  <div className="price">
                    <Price price={product.price} />
                  </div>
                </div>
              }

              {currentDiscount && !validCoupon &&
                <ProductDiscountPrice
                  price={product.price}
                  currentDiscount={currentDiscount}
                  hideDiscount={product.hideDiscount}
                  soldOut={product.soldOut}
                  slug={product.slug}
                />
              }

              <div className="button-wishlist-container">
                <ProductAddToCartBtn oldStyle product={product} />

                <WishlistButton
                  noText
                  slug={product.slug}
                  product={product}
                  productId={product._id}
                />
              </div>

              <RegionLocked
                included={product.regions_included}
                excluded={product.regions_excluded}
              />

              <div className="mt-3">
                <PaymentCards />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="GiftCardPage__contentContainer container">
        <ProductMessages product={product} />

        <ProductAboutSection
          product={product}
          name={product.name}
          systems={product.platforms}
          requirements={product.platform_specs}
          ztormGame={!!product.ztormId}
          catalina={product.catalina || false}
        />

        {!pathname.includes('/preview/') &&
          <HowToGiftBanner type={product.display_type} />
        }

        <LegalNotice notice={product.notice} />
      </div>
    </main>
  );
};


GiftCardView.propTypes = {
  product: PropTypes.shape({}),
};

GiftCardView.defaultProps = {
  product: {},
};

export default GiftCardView;
